import { Html, useProgress, useAnimations, OrbitControls } from '@react-three/drei';
import { Canvas, useLoader } from '@react-three/fiber';
import React, { Suspense, useRef, useEffect } from 'react';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import model from './models/animation_file_V3.glb';

function Loader() {
  const { progress } = useProgress();
  return <Html center>{progress} % loaded</Html>;
}

function Model({ playAnimation, isPaused }) {
  const gltf = useLoader(GLTFLoader, model);
  const ref = useRef();
  const { actions, names } = useAnimations(gltf.animations, ref);

  useEffect(() => {
    // Console log available animations for demo reference
    if (names.length > 0) {
      console.log('=== AVAILABLE ANIMATIONS ===');
      console.log('Animation names:', names);
      console.log('Total animations found:', names.length);
      names.forEach((name, index) => {
        console.log(`${index + 1}. ${name}`);
      });
      console.log('============================');
    }

    // Play hello animation once when model loads
    if (names.length > 0) {
      const action = actions[names[0]];
      if (action) {
        action.reset();
        action.setLoop(2201, 1); // Play once only
        action.play();
        
        // Stop the animation after it completes
        action.getMixer().addEventListener('finished', () => {
          action.stop();
        });
      }
    }
  }, [actions, names, gltf.scene]);

  useEffect(() => {
    // Handle play animation trigger
    if (playAnimation && names.length > 0) {
      const action = actions[names[0]];
      if (action) {
        action.reset().play();
      }
    }
  }, [actions, names, playAnimation]);

  useEffect(() => {
    // Handle pause/resume
    if (names.length > 0) {
      const action = actions[names[0]];
      if (action) {
        if (isPaused) {
          action.paused = true;
        } else {
          action.paused = false;
        }
      }
    }
  }, [actions, names, isPaused]);

  return (
    <primitive 
      ref={ref}
      object={gltf.scene} 
      position={[0, -1.4, 0]} 
      rotation={[0, Math.PI * 0.25, 0]}
      scale={[1.3, 1.4, 1.3]}
    />
  );
}

function SceneContent({ playAnimation, isPaused }) {
  return (
    <>
      <ambientLight intensity={0.5} />
      <directionalLight 
        position={[2, 5, 2]} 
        intensity={1} 
      />
      <Model playAnimation={playAnimation} isPaused={isPaused} />
      <OrbitControls 
        enablePan={false}
        enableZoom={false}
        enableRotate={true}
        minPolarAngle={Math.PI / 2}
        maxPolarAngle={Math.PI / 2}
        autoRotate={false}
      />
    </>
  );
}

const Scene = ({ playAnimation, isPaused }) => {
  return (
    <Canvas 
      camera={{ position: [2.59, 0.20, 2.51], fov: 50 }} 
      style={{ width: '100%', height: '100%' }}
      gl={{ 
        antialias: true, 
        powerPreference: "high-performance",
        preserveDrawingBuffer: false 
      }}
      onCreated={({ gl }) => {
        gl.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      }}
    >
      <Suspense fallback={<Loader />}>
        <SceneContent playAnimation={playAnimation} isPaused={isPaused} />
      </Suspense>
    </Canvas>
  );
};

export default Scene;